import { featureFlagsApi } from '@/lib/api';
import type { FeatureFlag } from '@mss_creator/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

export const FEATURE_FLAGS_QUERY_KEY = ['feature-flags'] as const;

const SAVED_FEEDBACK_MS = 3000;

/**
 * `PATCH /feature-flags/:key` for one flag. On success the cached list is
 * updated in place (no refetch, so the control never flickers back to the old
 * value) and `saved` stays true for a few seconds for the row's "Enregistré".
 * On failure the cache is untouched, which is the revert: the row keeps
 * showing the value the server still holds.
 */
export function useFlagMutation(key: string) {
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(false);
  const mutation = useMutation({
    mutationFn: (value: string) => featureFlagsApi.set(key, value),
    onSuccess: (flag) => {
      queryClient.setQueryData<FeatureFlag[]>(FEATURE_FLAGS_QUERY_KEY, (previous) =>
        previous ? previous.map((entry) => (entry.key === flag.key ? flag : entry)) : previous,
      );
    },
  });

  const { data } = mutation;
  useEffect(() => {
    if (!data) return;
    setSaved(true);
    const timer = setTimeout(() => setSaved(false), SAVED_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [data]);

  return { mutation, saved };
}
